"use client";

import { PORTALS } from ".constants/portals";
import {
  PortalModal,
  ModalHeader,
  ModalContent,
  usePortalContext,
  cn,
} from "@cloai/uikit";
import { motion } from "framer-motion";
import { map } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import { memo, useCallback } from "react";

import {
  PhotoSettings,
  useABtestContext,
  useUndressContext,
  useUndressSettingsContext,
} from "@/providers";
import { getLocalizedName } from "@/utils/getLocalizedName";

import { ItemCard } from "./components/ItemCard";
import { STYLES as STYLES_V1 } from "./styles";
import { STYLES as STYLES_V2 } from "./stylesV2";
import { MOTION_VARIANTS } from "./vars";

export const ClipsPortal = memo(() => {
  const t = useTranslations("UserFlowModal");
  const {
    clickFileId,
    clickUndressingFile,
    onReUseFaceObj,
    reUseFaceObj,
    updatePhotoSetting,
  } = useUndressContext();
  const { clips } = useUndressSettingsContext();
  const { closePortal, openPortal } = usePortalContext();
  const { AAB_RedesignV1 } = useABtestContext();

  const STYLES = AAB_RedesignV1 ? STYLES_V1 : STYLES_V2;

  const locale = useLocale();

  const handleClick = useCallback(
    (itemType: keyof PhotoSettings, itemId: string) => () => {
      updatePhotoSetting(clickFileId, itemType, itemId);
      closePortal();

      if (reUseFaceObj) {
        return onReUseFaceObj();
      }

      if (!clickUndressingFile) {
        return openPortal(PORTALS.USER_FLOW_UPLOAD);
      }
    },
    [
      clickFileId,
      clickUndressingFile,
      closePortal,
      onReUseFaceObj,
      openPortal,
      reUseFaceObj,
      updatePhotoSetting,
    ],
  );

  return (
    <PortalModal
      portalId={PORTALS.USER_FLOW_CLIPS}
      style={STYLES.portal.variants.medium}
    >
      <ModalHeader>{t("titles.chooseVideo")}</ModalHeader>

      <ModalContent className="@container">
        <motion.div
          variants={MOTION_VARIANTS.container}
          initial="hidden"
          animate="visible"
          role="grid"
          aria-label="Videos grid"
          className={cn(STYLES.grid.variants.big, STYLES.grid.animate)}
        >
          {map(clips, (item, Index) => (
            <motion.div
              key={item.id}
              variants={MOTION_VARIANTS.item}
              custom={Index}
              role="gridcell"
              aria-label={getLocalizedName(item, locale)}
            >
              <ItemCard
                item={item}
                index={Index}
                onClick={handleClick("clip", item.id)}
                variant="portrait"
              />
            </motion.div>
          ))}
        </motion.div>
      </ModalContent>
    </PortalModal>
  );
});

ClipsPortal.displayName = "ClipsPortal";
